// ============================================
// COMPANIES.JSX - All Companies List Page
// ============================================
// This page displays a grid of all companies in the admin panel
// Users can search companies and click to view detailed company information

import React, { useRef, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useGetAllCompaniesQuery, useDeleteCompanyMutation } from "../../../services/api";
import "./Companies.css";

// ============================================
// SVG ICON COMPONENTS
// ============================================

/* Search icon - Used in the search input */
const IconSearch = (props) => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
       stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
    <circle cx="11" cy="11" r="8" />
    <line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
);

/* Arrow icon - Used in the "Open Company" button */
const IconArrow = (props) => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
       stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
    <line x1="5" y1="12" x2="19" y2="12" />
    <polyline points="12 5 19 12 12 19" />
  </svg>
);

/* Trash icon - Used for delete button */
const IconTrash = (props) => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
       stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
    <line x1="10" y1="11" x2="10" y2="17" />
    <line x1="14" y1="11" x2="14" y2="17" />
  </svg>
);

// ============================================
// MAIN COMPONENT
// ============================================
export default function AdminCompanies() {
  // ============================================
  // STATE MANAGEMENT
  // ============================================
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [deleteModal, setDeleteModal] = useState({ isOpen: false, company: null });
  const inputRef = useRef(null);
  const navigate = useNavigate();

  // Delete mutation
  const [deleteCompany, { isLoading: isDeleting }] = useDeleteCompanyMutation();

  // ============================================
  // DEBOUNCE SEARCH QUERY
  // ============================================
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(query);
    }, 300);
    return () => clearTimeout(timer);
  }, [query]);

  // ============================================
  // FETCH COMPANIES FROM API
  // ============================================
  const {
    data: companiesData,
    isLoading,
    error
  } = useGetAllCompaniesQuery({
    page: 1,
    limit: 50,
    search: debouncedQuery || undefined,
  });

  const companies = companiesData?.companies || [];

  // ============================================
  // KEYBOARD SHORTCUT - Ctrl/Cmd + K
  // ============================================
  useEffect(() => {
    const onKey = (e) => {
      const isCtrlK = (e.ctrlKey || e.metaKey) && (e.key?.toLowerCase() === "k");
      if (isCtrlK) {
        e.preventDefault();
        inputRef.current?.focus();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // ============================================
  // DELETE HANDLER
  // ============================================
  const handleDelete = async () => {
    if (!deleteModal.company) return;

    try {
      await deleteCompany(deleteModal.company._id).unwrap();
      setDeleteModal({ isOpen: false, company: null });
    } catch (err) {
      console.error("Failed to delete company:", err);
    }
  };

  // ============================================
  // RENDER JSX
  // ============================================
  return (
    <div className="acomps">
      {/* ========== HEADER SECTION ========== */}
      <div className="acomps-top">
        <div className="welcome">← Back Home</div>
        <h1 className="username">All Companies</h1>
      </div>

      {/* ========== FILTER BAR ========== */}
      <div className="acomps-filterbar">
        <div className="acomps-count">
          Showing All Companies – <strong>{String(companies.length).padStart(2, "0")}</strong>
        </div>

        <div className="acomps-search">
          <IconSearch />
          <input
            ref={inputRef}
            type="text"
            placeholder="Search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          <span className="key-hint">Ctrl K</span>
        </div>
      </div>

      {/* ========== LOADING STATE ========== */}
      {isLoading && (
        <div className="acomps-loading">
          Loading companies...
        </div>
      )}

      {/* ========== ERROR STATE ========== */}
      {error && (
        <div className="acomps-error">
          Failed to load companies. Please try again.
        </div>
      )}

      {/* ========== EMPTY STATE ========== */}
      {!isLoading && !error && companies.length === 0 && (
        <div className="acomps-empty">
          No companies found.
        </div>
      )}

      {/* ========== COMPANY CARDS GRID ========== */}
      {!isLoading && !error && companies.length > 0 && (
        <div className="acomps-grid">
          {companies.map((company) => (
            <div className="acomps-card" key={company._id}>
              <div className="acomps-card-head">
                <div className="acomps-logo-placeholder">
                  {company.name.charAt(0).toUpperCase()}
                </div>
                <div className="acomps-meta">
                  <div className="acomps-name">{company.name}</div>
                  <div className="acomps-sub">
                    {company.memberCount || 0} members
                    {company.isActive ? (
                      <span className="status-badge active">Active</span>
                    ) : (
                      <span className="status-badge inactive">Inactive</span>
                    )}
                  </div>
                </div>
                <button
                  className="acomps-delete-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    setDeleteModal({ isOpen: true, company });
                  }}
                  title="Delete company"
                >
                  <IconTrash />
                </button>
              </div>

              <div className="acomps-card-info">
                {company.ownerId && (
                  <div className="acomps-owner">
                    Owner: {company.ownerId.firstName} {company.ownerId.lastName}
                  </div>
                )}
                {company.inviteCode && (
                  <div className="acomps-invite">
                    Invite Code: <code>{company.inviteCode}</code>
                  </div>
                )}
              </div>

              <button
                className="btn full"
                onClick={() => navigate(`/admin/companies/${company._id}`)}
              >
                Open Company <IconArrow />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* ========== DELETE CONFIRMATION MODAL ========== */}
      {deleteModal.isOpen && (
        <div className="acomps-modal-overlay" onClick={() => setDeleteModal({ isOpen: false, company: null })}>
          <div className="acomps-modal" onClick={(e) => e.stopPropagation()}>
            <div className="acomps-modal-header">
              <h3>Delete Company</h3>
            </div>
            <div className="acomps-modal-body">
              <p>
                Are you sure you want to delete <strong>{deleteModal.company?.name}</strong>?
              </p>
              <p className="acomps-modal-warning">
                This action cannot be undone. All {deleteModal.company?.memberCount || 0} members will be orphaned and can join another company using an invite code.
              </p>
            </div>
            <div className="acomps-modal-actions">
              <button
                className="btn secondary"
                onClick={() => setDeleteModal({ isOpen: false, company: null })}
                disabled={isDeleting}
              >
                Cancel
              </button>
              <button
                className="btn danger"
                onClick={handleDelete}
                disabled={isDeleting}
              >
                {isDeleting ? "Deleting..." : "Delete Company"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
